export type StoreAccess = {
  accessMode: 'marketplace' | 'store';
  empresaid: string | null;
};

export type StoreEntry = StoreAccess & { lookup: string | null; slug: string | null; error: string };

export const normalizeEmpresaId = (value: unknown): string | null => {
  const raw = String(value || '').trim().replace(/^empresa#/, '');
  return /^[a-zA-Z0-9-]+$/.test(raw) ? `empresa#${raw}` : null;
};

const get = (storage: Storage, key: string) => {
  try { return storage.getItem(key); } catch { return null; }
};
const set = (storage: Storage, key: string, value: string | null) => {
  try {
    if (value === null) storage.removeItem(key); else storage.setItem(key, value);
  } catch { /* A seleção ativa permanece no contexto da aplicação. */ }
};

// Compatibilidade com o ID antigo. O modo de acesso é definido pelo link,
// e a seleção durante a navegação pertence apenas ao contexto da aba.
export const readStoreAccess = (
  storage: Storage = window.localStorage
): StoreAccess => ({ accessMode: 'marketplace', empresaid: normalizeEmpresaId(get(storage, 'empresaid')) });

export const saveStoreAccess = (
  access: StoreAccess,
  storage: Storage = window.localStorage
) => {
  const empresaid = normalizeEmpresaId(access.empresaid);
  if (!['marketplace', 'store'].includes(access.accessMode)
    || (access.empresaid !== null && !empresaid) || (access.accessMode === 'store' && !empresaid)) return;
  set(storage, 'empresaid', empresaid);
};

export const resolveStoreEntry = (
  location: Pick<Location, 'hostname' | 'pathname' | 'search'> = window.location,
  storage: Storage = window.localStorage
): StoreEntry => {
  const subdomain = location.hostname.toLowerCase().split('.')[0];
  const ignored = ['localhost', 'xeguei', 'www', 'cliente', 'empresa', '127', '192', '[::1]'];
  const slug = subdomain && !ignored.includes(subdomain) && !/^\d+$/.test(subdomain) ? subdomain : null;
  if (slug) return { accessMode: 'store', empresaid: null, lookup: slug, slug, error: '' };

  const params = new URLSearchParams(location.search);
  const explicitPath = location.pathname.match(/^\/empresaid=(.*)$/i);
  const pathId = explicitPath ? explicitPath[1].replace(/\/$/, '')
    : location.pathname.match(/^\/([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i)?.[1];
  if (params.has('empresaid') || pathId !== undefined) {
    let raw = params.has('empresaid') ? params.get('empresaid') : pathId;
    if (!params.has('empresaid')) {
      try { raw = decodeURIComponent(raw || ''); } catch { raw = null; }
    }
    const empresaid = normalizeEmpresaId(raw);
    return { accessMode: 'store', empresaid, lookup: empresaid, slug: null,
      error: empresaid ? '' : 'O link desta loja é inválido.' };
  }
  const saved = readStoreAccess(storage);
  return { ...saved, lookup: saved.empresaid, slug: null, error: '' };
};

export const getGuestStoreDestination = (entry: StoreEntry, pathname: string) => {
  if (entry.accessMode === 'marketplace') return entry.empresaid || pathname === '/pedido' ? '/pedido' : '/primeiroacesso';
  if (entry.slug || !entry.empresaid) return '/pedido';
  return `/pedido?empresaid=${encodeURIComponent(entry.empresaid.replace(/^empresa#/, ''))}`;
};

export const clearStoreOrderTerms = (storage: Storage = window.localStorage) => {
  ['taxaEntrega', 'tempoEntrega', 'tipoAgendamento', 'dataAgendamento', 'horaAgendamento']
    .forEach(key => set(storage, key, null));
};
